import { useEffect, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageBand } from "@/components/kit/PageBand";
import { api, routes } from "@/lib/ppcp";
import { useProgress } from "@/lib/progress";
import { KIT, PAGES } from "@/constants/testIds";

/** A quiet pause between weeks: warm, specific, never gamified. No badges, no streaks. */
const NOTE = {
  1: {
    title: "One week done",
    line: "The first week is the hardest, and it's behind you. Your kitchen is stocked, the routine is starting to feel familiar, and you already know which breakfasts you like best.",
    ahead: "Week 2 leans into fibre-rich meals — methi parathas, moong dal chillas and lighter dinners.",
  },
  2: {
    title: "Two weeks in",
    line: "Fourteen days of home-cooked meals. This is the point where the plan usually stops feeling like an effort and starts feeling like how you eat.",
    ahead: "Week 3 stays filling with hearty vegetables — smoky baingan bharta and lauki chana dal.",
  },
  3: {
    title: "Three weeks strong",
    line: "Three weeks is long enough for a habit to settle. Whatever the scales say, your portions, your oil and your protein have all quietly changed.",
    ahead: "Week 4 is simple and repeatable on purpose — the meals you'll keep after Day 30.",
  },
};

export default function WeekMilestone() {
  const { slug, week } = useParams();
  const navigate = useNavigate();
  const w = Number(week);
  const { summary } = useProgress();
  const [title, setTitle] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api.get(`/programs/${slug}`).then((res) => setTitle(res.data.program.title)).catch(() => {});
  }, [slug]);

  const note = NOTE[w] ?? NOTE[1];
  const nextDay = w * 7 + 1;
  const done = summary?.days_complete ?? w * 7;
  const total = summary?.total_days ?? 30;

  return (
    <AppShell
      title={note.title}
      slug={slug}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: title ?? "Program", to: routes.program(slug) },
        { label: `Week ${w} complete` },
      ]}
    >
      <div data-testid={PAGES.weekMilestone} className="contents">
        <PageBand
          eyebrow={`Week ${w} of ${Math.ceil(total / 7)}`}
          title={note.title}
          accent={note.title.split(" ")[0]}
          intro={note.line}
        />

        <section className="ps-card ps-card-pad space-y-6">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-5">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-brand-green/10">
              <Check className="h-8 w-8 text-brand-green" strokeWidth={2.5} />
            </span>
            <div>
              <p className="font-display text-3xl font-semibold leading-none text-brand-teal">
                {done} <span className="text-xl text-brand-teal/40">/ {total} days</span>
              </p>
              <p className="mt-2 text-[15px] font-semibold uppercase tracking-[0.14em] text-brand-teal/50">
                {Math.round((done / total) * 100)}% of the plan
              </p>
            </div>
          </div>

          <div className="h-3 w-full overflow-hidden rounded-full bg-brand-teal/[0.08]">
            <div
              className="h-full rounded-full bg-brand-green transition-[width] duration-700"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>

          <p className="max-w-prose text-[17px] leading-[1.8] text-brand-teal/75">{note.ahead}</p>
        </section>

        {/* Plan Overview and My Plans live in the persistent bottom navigation. */}
        <button
          data-testid={KIT.milestoneContinue}
          onClick={() => navigate(routes.day(slug, nextDay))}
          className="ps-tap group flex w-full items-center justify-between gap-4 rounded-[22px] bg-brand-teal px-7 py-6 text-left shadow-card transition-colors hover:bg-brand-tealDeep"
        >
          <span>
            <span className="block text-[12px] font-bold uppercase tracking-[0.2em] text-white/65">
              Week {w + 1} starts here
            </span>
            <span className="mt-1 block font-display text-xl font-semibold text-white sm:text-2xl">
              Continue to Day {nextDay}
            </span>
          </span>
          <ArrowRight className="h-7 w-7 shrink-0 text-white transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </AppShell>
  );
}
